import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { StatusBadge, Spinner } from "../../components/Brand";
import { STATUS_TIMELINE, STATUS_META, currency, fmtDate } from "../../lib/status";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import BookingBillingPanel from "./BookingBillingPanel";

export default function BookingDetail() {
    const { id } = useParams();
    const [b, setB] = useState(null);
    const [rating, setRating] = useState(0);
    const [review, setReview] = useState("");
    const nav = useNavigate();
    const { user } = useAuth();
    const isAdminLike = user && ["super_admin", "admin", "company_admin", "operations", "finance"].includes(user.role);

    const load = async () => {
        const { data } = await api.get(`/bookings/${id}`);
        setB(data);
        setRating(data.rating || 0);
        setReview(data.review || "");
    };

    useEffect(() => {
        load();
    }, [id]);

    if (!b) return <Spinner />;

    const reachedIdx = STATUS_TIMELINE.indexOf(b.status);
    const isCancelled = ["cancelled", "refunded"].includes(b.status);

    const cancel = async () => {
        if (!window.confirm("Cancel this booking?")) return;
        try {
            await api.post(`/bookings/${b.id}/status`, {
                status: "cancelled",
                note: "Cancelled by customer",
            });
            toast.success("Booking cancelled");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cannot cancel");
        }
    };

    const submitRating = async () => {
        if (!rating) return toast.error("Pick a rating first");
        try {
            await api.post(`/bookings/${b.id}/rate`, { rating, review });
            toast.success("Thanks for your feedback!");
            load();
        } catch (e) {
            toast.error("Could not submit");
        }
    };

    const markCustomerConfirmed = async () => {
        try {
            await api.post(`/bookings/${b.id}/status`, { status: "customer_confirmed" });
            await api.post(`/bookings/${b.id}/status`, { status: "invoice_generated" });
            await api.post(`/bookings/${b.id}/status`, { status: "payment_completed" });
            toast.success("Job confirmed & paid");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not confirm");
        }
    };

    const payWithStripe = async () => {
        try {
            const { data } = await api.post("/payments/stripe/checkout", {
                booking_id: b.id,
                origin_url: window.location.origin,
            });
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Could not start Stripe checkout");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Stripe unavailable");
        }
    };

    const decideSpareParts = async (decision) => {
        try {
            await api.post(`/bookings/${b.id}/spare-parts/decide`, { decision });
            toast.success(decision === "approved" ? "Spare parts approved" : "Spare parts rejected");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not submit");
        }
    };

    return (
        <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
                <button
                    onClick={() => nav(-1)}
                    className="text-sm text-[#555] hover:text-[#180A21] mb-4"
                >
                    ← Back
                </button>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="lx-label text-[#B2912A]">{b.booking_number}</span>
                    <StatusBadge status={b.status} />
                </div>
                <h1 className="font-serif text-4xl text-[#180A21] mb-2">
                    {b.service?.name}
                </h1>
                <div className="text-[#555] mb-8">{fmtDate(b.scheduled_at)}</div>

                {/* Timeline */}
                <div className="lx-card p-7 mb-6">
                    <div className="lx-label text-[#B2912A] mb-6">Live tracking</div>
                    {isCancelled ? (
                        <div className="text-center py-8 text-[#555]">
                            <div className="font-serif text-2xl text-[#180A21] mb-2">
                                Booking {b.status}
                            </div>
                            {b.cancellation_reason && (
                                <div className="text-sm">{b.cancellation_reason}</div>
                            )}
                        </div>
                    ) : (
                        <ol className="space-y-4">
                            {STATUS_TIMELINE.map((s, i) => {
                                const reached = i <= reachedIdx;
                                const active = i === reachedIdx;
                                const meta = STATUS_META[s];
                                return (
                                    <li key={s} className="flex items-start gap-4">
                                        <div className="flex flex-col items-center">
                                            <div
                                                className={`w-3 h-3 rounded-full ${
                                                    reached
                                                        ? "bg-[#D4AF37]"
                                                        : "bg-white border-2 border-[#E5E2DC]"
                                                } ${active ? "ring-4 ring-[#D4AF37]/20" : ""}`}
                                            />
                                            {i < STATUS_TIMELINE.length - 1 && (
                                                <div className="w-px h-8 lx-step-line" />
                                            )}
                                        </div>
                                        <div className="pb-2">
                                            <div
                                                className={`text-sm font-medium ${
                                                    reached ? "text-[#180A21]" : "text-[#555]"
                                                }`}
                                            >
                                                {meta.label}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>

                {/* Pending spare parts (customer approves/rejects) */}
                {b.spare_parts_status === "pending" && b.pending_spare_parts?.length > 0 && (
                    <div className="lx-card p-7 mb-6 border-l-4 border-[#D4AF37]" data-testid="customer-spare-card">
                        <div className="lx-label text-[#B2912A] mb-3">
                            Technician proposes extra charges
                        </div>
                        <p className="text-sm text-[#555] mb-3">
                            Approve to add these to the final invoice.
                        </p>
                        <div className="space-y-1 mb-4">
                            {b.pending_spare_parts.map((p, i) => (
                                <div
                                    key={i}
                                    className="flex justify-between text-sm text-[#180A21]"
                                >
                                    <span>{p.name}</span>
                                    <span className="font-medium">
                                        {currency(p.price, b.currency)}
                                    </span>
                                </div>
                            ))}
                            <div className="flex justify-between pt-2 mt-2 border-t border-[#F3F2F0] text-sm">
                                <span>Extra total</span>
                                <span className="font-serif text-lg text-[#180A21]">
                                    {currency(
                                        b.pending_spare_parts.reduce(
                                            (s, p) => s + (p.price || 0),
                                            0,
                                        ),
                                        b.currency,
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="customer-spare-reject"
                                onClick={() => decideSpareParts("rejected")}
                                className="flex-1 py-2.5 border border-red-300 text-red-700 text-sm"
                            >
                                Reject
                            </button>
                            <button
                                data-testid="customer-spare-approve"
                                onClick={() => decideSpareParts("approved")}
                                className="lx-btn-gold flex-1 py-2.5 text-sm"
                            >
                                Approve
                            </button>
                        </div>
                    </div>
                )}

                {/* OTP display for active verification */}
                {b.status === "job_started" && b.otp_plain && (
                    <div
                        className="lx-card p-7 mb-6 bg-[#180A21] text-white"
                        data-testid="customer-otp-card"
                    >
                        <div className="lx-label text-[#D4AF37] mb-2">
                            Your verification code
                        </div>
                        <p className="text-sm text-white/70 mb-4">
                            Share this 6-digit code with your technician to confirm job
                            completion.
                        </p>
                        <div
                            className="font-mono text-5xl tracking-widest text-[#D4AF37] text-center py-4 border-y border-[#2B1438]"
                            data-testid="customer-otp-value"
                        >
                            {b.otp_plain}
                        </div>
                        {b.otp_expires_at && (
                            <p className="text-xs text-white/40 mt-3 text-center">
                                Valid until{" "}
                                {new Date(b.otp_expires_at).toLocaleTimeString()}
                            </p>
                        )}
                    </div>
                )}

                {/* Technician */}
                {b.technician && (
                    <div className="lx-card p-7 mb-6">
                        <div className="lx-label text-[#B2912A] mb-4">Your technician</div>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-[#F3F2F0] overflow-hidden">
                                {b.technician.avatar_url && (
                                    <img
                                        src={b.technician.avatar_url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="font-serif text-xl text-[#180A21]">
                                    {b.technician.name}
                                </div>
                                <div className="text-sm text-[#555]">
                                    ★ {b.technician.rating_avg?.toFixed(1)} ·{" "}
                                    {b.technician.phone || "Contact via chat"}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Billing (admin-only): quotations + tax invoices linked to this booking */}
                {isAdminLike && <BookingBillingPanel bookingId={b.id} />}

                {/* Photos */}
                {(b.before_photos?.length > 0 || b.after_photos?.length > 0) && (
                    <div className="lx-card p-7 mb-6">
                        <div className="lx-label text-[#B2912A] mb-4">Job photos</div>
                        <div className="grid grid-cols-2 gap-4">
                            {b.before_photos?.length > 0 && (
                                <div>
                                    <div className="lx-label text-[#555] mb-2">Before</div>
                                    {b.before_photos.map((p, i) => (
                                        <img key={i} src={p} alt="" className="w-full mb-2" />
                                    ))}
                                </div>
                            )}
                            {b.after_photos?.length > 0 && (
                                <div>
                                    <div className="lx-label text-[#555] mb-2">After</div>
                                    {b.after_photos.map((p, i) => (
                                        <img key={i} src={p} alt="" className="w-full mb-2" />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Rating after completion */}
                {(b.status === "payment_completed" ||
                    b.status === "invoice_generated" ||
                    b.status === "job_completed") && (
                    <div className="lx-card p-7">
                        <div className="lx-label text-[#B2912A] mb-4">Rate your experience</div>
                        <div className="flex gap-2 mb-4">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    data-testid={`rate-${n}`}
                                    onClick={() => setRating(n)}
                                    className="text-3xl transition hover:scale-110"
                                    style={{ color: n <= rating ? "#D4AF37" : "#E5E2DC" }}
                                >
                                    ★
                                </button>
                            ))}
                        </div>
                        <textarea
                            data-testid="review-input"
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                            placeholder="Share your experience…"
                            rows={3}
                            className="w-full p-3 border border-[#E5E2DC] focus:border-[#D4AF37] outline-none"
                        />
                        <button
                            data-testid="rating-submit-btn"
                            onClick={submitRating}
                            className="lx-btn-primary px-5 py-2.5 mt-3"
                        >
                            Submit review
                        </button>
                    </div>
                )}
            </div>

            <aside className="lg:col-span-5 space-y-4">
                <div className="lx-card p-7">
                    <div className="lx-label text-[#B2912A] mb-5">Payment summary</div>
                    <div className="space-y-2 text-sm">
                        <Row
                            label="Subtotal"
                            value={currency(b.base_amount, b.currency)}
                        />
                        {b.discount > 0 && (
                            <Row
                                label="Discount"
                                value={`- ${currency(b.discount, b.currency)}`}
                            />
                        )}
                        <Row label="Tax" value={currency(b.tax, b.currency)} />
                        <div className="border-t border-[#F3F2F0] pt-3 mt-3 flex items-center justify-between">
                            <div className="lx-label text-[#180A21]">Total</div>
                            <div className="font-serif text-2xl text-[#180A21]">
                                {currency(b.total_amount, b.currency)}
                            </div>
                        </div>
                        <div className="lx-label text-[#555] pt-2">
                            {b.payment_method} · {b.payment_status}
                        </div>
                    </div>
                    {b.payment_status !== "paid" && !isCancelled && (
                        <button
                            data-testid="pay-stripe-btn"
                            onClick={payWithStripe}
                            className="mt-5 w-full py-3 bg-[#635BFF] hover:bg-[#524AFF] text-white text-sm font-medium tracking-wide transition-colors"
                        >
                            Pay {currency(b.total_amount, b.currency)} with Stripe
                        </button>
                    )}
                </div>

                {/* Address */}
                <div className="lx-card p-7">
                    <div className="lx-label text-[#B2912A] mb-3">Service Address</div>
                    <div className="text-sm text-[#180A21]">{b.address?.line1}</div>
                    {b.address?.line2 && (
                        <div className="text-sm text-[#555]">{b.address.line2}</div>
                    )}
                    <div className="text-sm text-[#555] mt-1">
                        {b.address?.city}, {b.address?.country}
                    </div>
                </div>

                {/* Customer actions */}
                {b.status === "job_completed" && (
                    <button
                        data-testid="customer-confirm-job-btn"
                        onClick={markCustomerConfirmed}
                        className="lx-btn-gold w-full py-3"
                    >
                        Confirm completion & Pay
                    </button>
                )}
                {!isCancelled &&
                    !["job_started", "job_completed", "payment_completed", "invoice_generated"].includes(
                        b.status,
                    ) && (
                        <button
                            data-testid="cancel-booking-btn"
                            onClick={cancel}
                            className="w-full py-3 border border-red-300 text-red-700 text-sm"
                        >
                            Cancel booking
                        </button>
                    )}
            </aside>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[#555]">{label}</span>
            <span className="text-[#180A21] font-medium">{value}</span>
        </div>
    );
}
